/**
 * @fileoverview `buildDiscoverHeroMeta` — derive the Discover hero strip's
 * window label from the URL `date` param the page already has.
 *
 * The hero strip is a Server Component band rendered once per request. The
 * window string is frozen at render time and passed as a plain prop, so it
 * never recomputes on the client — no hydration drift, no new data fetch
 * (the single-fetch cold-load invariant in `lib/discover/queryArgs.ts` stays
 * intact). When the window can't be derived cleanly we fall back to a neutral,
 * non-fabricated label rather than inventing a value.
 *
 * Window labels are sentence case. The strip's meta line is not uppercase.
 * An unfiltered catalog reads "Upcoming" — never a fabricated 30-day window.
 *
 * WEB-1223: the strip does not print a freshness phrase. `meta.lastUpdatedAt`
 * is stamped when the response is built, so it is not catalogue age. The field
 * stays on the wire for other consumers.
 */

import { DATE_WINDOW_PRESETS, dateWindowPreset } from '@loci/validation';

/**
 * Sentence-case active-window label for each URL `date` keyword. Exported so
 * the results-bar count line (WEB-790 / PAR-25) shares this vocabulary.
 */
export const DATE_WINDOW_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  // API-1820 — labels come from the shared window module. `this-week` reads
  // "This week" while `facets.scopeKey` has not landed; when the API sends an
  // ISO scope key, that span wins (WEB-946).
  DATE_WINDOW_PRESETS.map((preset) => [preset, dateWindowPreset(preset).label]),
);

/** Unfiltered / unrecognized window. A missing date filter is the whole upcoming catalog. */
const UNWINDOWED_LABEL = 'Upcoming';

export interface DiscoverHeroMeta {
  /** Active time-window label, e.g. `"Tonight"` or `"Upcoming"`. */
  readonly window: string;
}

/**
 * Map a URL `date` value to its window label.
 *
 * Recognized preset keywords map to a friendly label. No filter, the
 * `'upcoming'` token, and an unrecognized value (including a raw ISO range)
 * read {@link UNWINDOWED_LABEL} rather than claiming "Next 30 days" for a
 * catalog that is not a 30-day window. `next30` still maps through
 * {@link DATE_WINDOW_LABELS}.
 */
export function formatHeroWindow(date: string | undefined): string {
  if (date === undefined || date === '' || date === 'upcoming') return UNWINDOWED_LABEL;
  return DATE_WINDOW_LABELS[date] ?? UNWINDOWED_LABEL;
}

/**
 * Build the {@link DiscoverHeroMeta} window from the URL `date` param.
 */
export function buildDiscoverHeroMeta(date: string | undefined): DiscoverHeroMeta {
  return {
    window: formatHeroWindow(date),
  };
}
